import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdVideocam } from 'react-icons/md';
import PageLayout from 'components/page-layout';
import PageCard from 'components/page-card';
import EmptyState from 'components/states/EmptyState';
import SkeletonTable from 'components/states/SkeletonTable';
import { VideoIllustration } from 'components/illustrations';
import VideoPlayerPanel from './components/VideoPlayerPanel';
import EventTimeline from './components/EventTimeline';
import PlayerStatsCards from './components/PlayerStatsCards';
import CourtHeatmap from './components/CourtHeatmap';
import ShotChart from './components/ShotChart';
import ProcessingTimeline from './components/ProcessingTimeline';
import { EVENT_COLORS } from './types';
import { CourtRecordingStatus } from '../../models/court-recording/dto/court-recording.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import { useListCourtRecordings } from './hooks/useListCourtRecordings';
import { useGetCourtRecordingAnalysis } from './hooks/useGetCourtRecordingAnalysis';
import { useGetCourtRecordingVideoUrl } from './hooks/useGetCourtRecordingVideoUrl';
import { daysKeptBeforeExpiry } from './utils/expiry';
import { translate } from '../../i18n';

export default function CourtRecordingDetailPage() {
  const { recordingId } = useParams<{ recordingId: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id || '';

  // This page finds its recording by scanning the ORG-gated list, so the list
  // read's four states all land on `!recording` unless they are separated
  // first. Two of them were wrong here, in opposite directions:
  //   never-issued — `enabled: Boolean(organizationId)` is unsatisfied with no
  //     organization selected, and React Query v4 reports a disabled query as
  //     `isLoading: true`, so the raw flag below showed a skeleton FOREVER.
  //     (The same trap is already named 30 lines down for the video-URL query;
  //     it applies to this query too, and only that one had been fixed.)
  //   unreadable — the list route 403s for an account without an admin
  //     membership in the selected org, and this page had NO error arm at all,
  //     so a refused read fell through to "Recording not found" — a confident
  //     claim that the operator's recording does not exist, about a read that
  //     was refused. It is the sibling of the incident the venue-agents page
  //     had on 2026-09-12.
  const {
    data: recordings,
    isInitialLoading: isListInitialLoading,
    isError: isListError,
  } = useListCourtRecordings(orgId);
  const listState = classifyRead(isListError, isListInitialLoading, recordings);
  const recording = recordings?.find((r) => r.id === recordingId) ?? null;

  const isCompleted = recording?.status === CourtRecordingStatus.COMPLETED;
  // Retention (B4): the sweep deleted this recording's blobs and stamped it.
  // Status is still COMPLETED, so without this the analysis read below would
  // fire, get a 410, and render "failed to load analysis" — a claim about a
  // read, about a video that is simply gone.
  const isExpired = Boolean(recording?.expiredAt);
  // `isInitialLoading`, not `isLoading` — the same v4 trap the video-URL read
  // thirty lines below already names, and the last read on this page still
  // reading it raw. This one is not a LIVE defect: the analysis query's gate is
  // `Boolean(recordingId) && isCompleted`, and the `!recording` and
  // `!isCompleted` early returns below both fire before the skeleton branch, so
  // no reachable state gets the permanent spinner today. What was wrong is that
  // the page held a PRIVATE COPY of the hook's `enabled` expression (D62) and
  // the raw flag was only safe *because of where those two returns sit* — a
  // coupling nothing stated and nothing pinned. Reorder the returns, or grow
  // the gate a second term, and the spinner comes back silently.
  // `isInitialLoading` is `isLoading && isFetching`, so it is already false for
  // a disabled query and the read answers the question itself.
  const {
    data: analysisData,
    isInitialLoading: isAnalysisLoading,
    isError: isAnalysisError,
  } = useGetCourtRecordingAnalysis(recordingId || '', isCompleted && !isExpired);
  // `hasVideo` rather than `Boolean(recording?.videoUrl)`: the URL is
  // tokenless, Azure-coupled and leaves the DTO in the storage epic's Phase 3.
  // The API computes this flag with exactly the old truthiness, so this page
  // behaves identically — including for an `awaitingUpload` recording, where
  // both the old and the new expression are `true`.
  const hasVideoUrl = Boolean(recording?.hasVideo);
  const {
    data: videoUrlData,
    isInitialLoading: isVideoUrlInitialLoading,
    isError: isVideoUrlError,
    refetch: refetchVideoUrl,
  } = useGetCourtRecordingVideoUrl(recordingId || '', hasVideoUrl);
  // The third state this read never had. `videoSrc` is null for a refused SAS
  // fetch exactly as it is for a recording with no clip, and the panel's only
  // non-playing, non-loading branch says "No video provided" — so a 403, a
  // timeout or a dead storage account told the operator the video does not
  // exist. Run 108 gave the LIST read on this page its three states and, as
  // its own docblock warns, a file fixed once for this class is not evidence
  // that its other reads were looked at.
  const videoUrlState = classifyRead(isVideoUrlError, isVideoUrlInitialLoading, videoUrlData);

  // Always reflects the latest fetched SAS URL — including background
  // revalidations react-query triggers on refocus/reconnect past staleTime —
  // instead of a set-once snapshot that goes stale after the 2h SAS expires.
  const videoSrc = videoUrlData?.url ?? null;
  // React Query v4 has no 'idle' status: a disabled query reports
  // isLoading=true forever since it never fetches, which is why a recording
  // with no video used to need `hasVideoUrl &&` here to reach the "no video"
  // placeholder instead of a permanent skeleton. `isInitialLoading` is
  // `isLoading && isFetching` and is therefore already false for a disabled
  // query, so the hook now answers that itself and this file no longer keeps a
  // private copy of its `enabled` expression (D62) — one that would have gone
  // quietly wrong the day the gate grew a second term.
  const isVideoUrlLoading = videoUrlState === 'loading';

  const goBack = () => navigate('/admin/court-recording');

  if (listState === 'never-issued') {
    return (
      <PageLayout>
        <PageCard title={translate('court_recording.recording_analysis')} showBackButton>
          <Box px="25px" pb="20px">
            <ScopePrompt gate="organization" />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  if (listState === 'loading') {
    return (
      <PageLayout>
        <SkeletonTable />
      </PageLayout>
    );
  }

  if (listState === 'unreadable') {
    return (
      <PageLayout>
        <PageCard title={translate('court_recording.recording_analysis')} showBackButton>
          <Box px="25px" pb="20px">
            <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack} mb={4}>
              {translate('court_recording.back_to_list')}
            </Button>
            <EmptyState
              title={translate('court_recording.failed_to_load')}
              illustration={<VideoIllustration />}
            />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  if (!recording) {
    return (
      <PageLayout>
        <PageCard title={translate('court_recording.recording_analysis')} showBackButton>
          <Box px="25px" pb="20px">
            <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack} mb={4}>
              {translate('court_recording.back_to_list')}
            </Button>
            <EmptyState
              title={translate('court_recording.recording_not_found') || 'Recording not found'}
              illustration={<VideoIllustration />}
            />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  if (recording.expiredAt) {
    const daysKept = daysKeptBeforeExpiry(recording.completedAt, recording.expiredAt);
    const date = new Date(recording.expiredAt).toLocaleDateString();
    return (
      <PageLayout>
        <PageCard
          title={`${translate('court_recording.recording_analysis')} — ${recording.courtName}`}
          showBackButton
          rightHeader={
            <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack}>
              {translate('court_recording.back_to_list')}
            </Button>
          }
        >
          <Box px="25px" pb="20px">
            <EmptyState
              title={translate('court_recording.video_expired_title')}
              description={
                daysKept !== null
                  ? translate('court_recording.video_expired_description', { date, days: daysKept })
                  : translate('court_recording.video_expired_description_no_days', { date })
              }
              illustration={<VideoIllustration />}
            />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  if (!isCompleted) {
    return (
      <PageLayout>
        <PageCard
          title={`${translate('court_recording.recording_analysis')} — ${recording.courtName}`}
          showBackButton
          rightHeader={
            <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack}>
              {translate('court_recording.back_to_list')}
            </Button>
          }
        >
          <Box px="25px" pb="20px">
            {/* Which recovery clause failed it (agent saw it and never
                uploaded / footage past the box buffer / no agent covers the
                court) lives only in `errorMessage` — `failureReason` is
                `agent_timeout` for all three. The list shows it as a tooltip;
                this is the page an operator opens to find out why. */}
            {recording.status === CourtRecordingStatus.FAILED &&
              (recording.errorMessage || recording.failureReason) && (
                <Text fontSize="sm" color="red.400" mb={4} data-testid="recording-failure">
                  <Text as="span" fontWeight="600">
                    {translate('court_recording.failure_reason')}
                  </Text>{' '}
                  <Text as="span">{recording.errorMessage || recording.failureReason}</Text>
                </Text>
              )}
            <ProcessingTimeline recording={recording} />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  if (isAnalysisLoading) {
    return (
      <PageLayout>
        <SkeletonTable />
      </PageLayout>
    );
  }

  if (isAnalysisError || !analysisData) {
    return (
      <PageLayout>
        <PageCard title={translate('court_recording.recording_analysis')} showBackButton>
          <Box px="25px" pb="20px">
            <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack} mb={4}>
              {translate('court_recording.back_to_list')}
            </Button>
            <EmptyState
              title={isAnalysisError ? translate('court_recording.failed_to_load_analysis') : translate('court_recording.analysis_not_available')}
              illustration={<VideoIllustration />}
            />
          </Box>
        </PageCard>
      </PageLayout>
    );
  }

  const eventLog = analysisData.events.slice(0, 50);

  return (
    <PageLayout>
      <PageCard
        title={`${translate('court_recording.analysis_title')} — ${recording.courtName}`}
        showBackButton
        rightHeader={
          <Button size="sm" variant="outline" leftIcon={<MdArrowBack />} onClick={goBack}>
            {translate('court_recording.back_to_list')}
          </Button>
        }
      >
        <Box px="25px" pb="20px">
          <PlayerStatsCards data={analysisData} />

          <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px" mt={6}>
            <Box>
              <Card p="15px">
                <VideoPlayerPanel
                  videoSrc={videoSrc}
                  videoRef={videoRef}
                  videoInfo={analysisData.video}
                  isLoading={isVideoUrlLoading}
                  isUnreadable={videoUrlState === 'unreadable'}
                  onVideoError={() => refetchVideoUrl()}
                />
                <EventTimeline
                  events={analysisData.events}
                  duration={analysisData.video.duration_seconds}
                  videoRef={videoRef}
                />
              </Card>
            </Box>

            <Box>
              <CourtHeatmap heatmaps={analysisData.heatmaps} />
            </Box>

            <Box>
              <ShotChart data={analysisData} />
            </Box>

            <Card p="15px">
              <Text fontSize="md" fontWeight="600" color={textColor} mb={3}>{translate('court_recording.event_log')}</Text>
              <Box overflowY="auto" maxH="320px">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>{translate('court_recording.time')}</Th>
                      <Th>{translate('court_recording.type')}</Th>
                      <Th>{translate('court_recording.frame')}</Th>
                      <Th>{translate('court_recording.details')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eventLog.map((e, i) => (
                      <Tr
                        key={i}
                        cursor={videoSrc ? 'pointer' : 'default'}
                        _hover={videoSrc ? { bg: hoverBg } : {}}
                        onClick={() => {
                          if (videoRef.current) {
                            videoRef.current.currentTime = e.time;
                          }
                        }}
                      >
                        <Td>{e.time.toFixed(1)}s</Td>
                        <Td>
                          <Flex align="center" gap={1}>
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="50%"
                              bg={EVENT_COLORS[e.type] || '#805AD5'}
                            />
                            {e.type.replace('_', ' ')}
                          </Flex>
                        </Td>
                        <Td>{e.frame}</Td>
                        <Td>
                          {e.position_m
                            ? `(${e.position_m[0]}m, ${e.position_m[1]}m)`
                            : e.position ? `(${e.position[0]}px, ${e.position[1]}px)` : ''}
                          {e.angle_change ? ` ${e.angle_change.toFixed(0)}°` : ''}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Card>
          </SimpleGrid>
        </Box>
      </PageCard>
    </PageLayout>
  );
}
